<template>
  <dialog
    class="overlay"
    aria-modal="true"
    tabindex="-1"
    data-testid="checkout-popup"
    :open="isVisible"
    :style="viewportStyle"
    @touchmove.self.prevent
    @wheel.self.prevent
  >
    <div
      v-show="dialogStore.loader"
      class="blocker"
      style="position: absolute; top: 0; left: 0; z-index: 20000"
    />
    <transition
      :name="modalSettings.transition"
    >
      <div
        v-show="isVisible"
        class="payment-modal"
        :class="{
          'payment-modal--managed-viewport': hasManagedViewport,
          'payment-modal--focus-space': hasFocusSpace,
        }"
        :style="paymentModalStyle"
      >
        <div
          ref="scrollContainer"
          class="payment-modal__container"
          @pointerdown.capture="cancelReveal"
          @touchstart.passive="cancelReveal"
          @wheel.passive="onManualScroll"
          @focusin="onFocusIn"
          @focusout="onFocusOut"
          @touchmove.passive="expandPaymentModal"
          @scroll.passive="onContainerScroll"
        >
          <div
            data-checkout-content
            class="payment-modal__content"
          >
            <div class="payment-modal__header">
              <h1
                v-if="title"
                :class="[
                  'h2_title text-center',
                  { 'payment-modal__title': !showDescription },
                  locale,
                ]"
              >
                {{ $t(isCheckoutVariantB ? 'lp_checkout_variant_b_title' : 'lp_v7_pop_up_title') }}
              </h1>
            </div>

            <button
              class="payment-modal__close"
              data-testid="close-checkout"
              :disabled="dialogStore.loader"
              @click="onClose"
            >
              <nuxt-icon name="close" />
            </button>

            <component
              :is="formComponent"
              v-if="isFormMounted"
              :configs="paymentData"
              :upsale="upsale"
              :show-description="showDescription"
              :stage="stage"
              :is-super-discount="isSuperDiscount"
              :selected-plan-id="selectedPlanId"
              :theme="theme"
              :theme-classes="themeClasses"
              @on-success="onSuccess"
              @action="emit('action')"
              @redirect-return="emit('redirect-return', $event)"
              @card-input-focus="onCardInputFocus"
              @card-input-blur="blurField"
            />
          </div>
        </div>
      </div>
    </transition>
  </dialog>
</template>

<script setup lang="ts">
import { getDeviceModel, isPixelFacebook } from '@core/base/utils/pixelFacebook';
import { IN_APP_BROWSER_UA } from '@core/base/common/constants/inAppBrowser';
import { useCheckoutViewport } from '../composables/useCheckoutViewport';
import { hasRedirectReturn } from '../utils/redirectReturn';

interface ModalSettings {
  transition: string;
  selfClick: boolean;
}

interface Props {
  form?: string | Component;
  title?: boolean;
  showDescription?: boolean;
  upsale?: boolean;
  stage?: string;
  selectedPlanId?: number;
  isSuperDiscount?: boolean;
  theme?: string;
  paymentModalHeight?: string;
  paymentData: PaymentModel;
  deferForm?: boolean;
  modalSettings?: ModalSettings;
  themeClasses?: { discount: string; regular: string };
}

const props = withDefaults(defineProps<Props>(), {
  form: 'BasicCheckoutForm',
  title: true,
  showDescription: true,
  upsale: false,
  stage: '',
  theme: '',
  isSuperDiscount: false,
  deferForm: false,
  modalSettings: () => ({
    transition: 'slide-bottom',
    selfClick: true,
  }),
  themeClasses: () => ({ discount: 'red', regular: 'blue' }),
});

const emit = defineEmits(['action', 'closed', 'on-success', 'redirect-return']);
const { isVisible, showCheckout } = useCheckoutModal();
const wasOpened = ref(false);

watch(isVisible, (visible) => {
  if (visible) wasOpened.value = true;
}, { immediate: true });

const isFormMounted = computed(() =>
  !props.deferForm || wasOpened.value || hasRedirectReturn());
const scrollContainer = ref<HTMLElement | null>(null);
const { isAndroid, isIos } = useDevice();
const hasManagedViewport = isAndroid || isIos;
const { isTikTokInApp } = useHelpers();
const isPixelFacebookDevice = ref(import.meta.client && isPixelFacebook(navigator.userAgent));
const usesSharedCardForm = computed(() => props.form === 'BasicCheckoutForm' || props.form === 'WhiteCheckoutForm');
const needsFocusSpace = computed(() =>
  isAndroid && usesSharedCardForm.value && (isTikTokInApp.value || isPixelFacebookDevice.value),
);
const {
  viewportStyle,
  hasFocusSpace,
  focusField,
  blurField,
  cancelReveal,
  checkFieldVisible,
  onFocusIn,
  onFocusOut,
} = useCheckoutViewport(scrollContainer, isVisible, hasManagedViewport, needsFocusSpace, isIos);
const dialogStore = useDialogStore();
const { locale } = useI18n();
const { isCheckoutVariantB } = useCheckoutFormVariant();
const checkoutHistoryState = 'checkout-modal-open';
let hasHistoryState = false;
const currentPaymentModalHeight = ref<string | undefined>(props.paymentModalHeight);

const paymentModalStyle = computed(() =>
  currentPaymentModalHeight.value
    ? { '--payment-modal-height': currentPaymentModalHeight.value }
    : undefined,
);
function onCardInputFocus(field: HTMLElement | null): void {
  expandPaymentModal();
  focusField(field);
}

function onManualScroll(): void {
  cancelReveal();
  expandPaymentModal();
}

function onContainerScroll(): void {
  expandPaymentModal();
  checkFieldVisible();
}

function expandPaymentModal(): void {
  if (!props.paymentModalHeight) {
    return;
  }

  currentPaymentModalHeight.value = '95dvh';
}

const formComponent = computed(() => {
  if (!props.form || props.form === 'BasicCheckoutForm') {
    return defineAsyncComponent(() => import('./BasicCheckoutForm.vue'));
  }

  if (props.form === 'WhiteCheckoutForm') {
    return defineAsyncComponent(() => import('./WhiteCheckoutForm.vue'));
  }

  return props.form;
});

const handlePopState = () => {
  if (!isVisible.value) {
    return;
  }

  if (dialogStore.loader) {
    globalThis.history.pushState({ [checkoutHistoryState]: true }, '', globalThis.location.href);
    return;
  }

  hasHistoryState = false;
  onClose();
};

function onClose() {
  showCheckout(false);
  emit('closed');
}
function onSuccess() {
  emit('on-success');
  emit('action');
}

if (import.meta.client) {
  watch(isVisible, (visible) => {
    if (visible) {
      currentPaymentModalHeight.value = props.paymentModalHeight;
      globalThis.history.pushState({ [checkoutHistoryState]: true }, '', globalThis.location.href);
      globalThis.addEventListener('popstate', handlePopState);
      hasHistoryState = true;
      return;
    }

    globalThis.removeEventListener('popstate', handlePopState);

    if (hasHistoryState) {
      hasHistoryState = false;
      globalThis.history.back();
    }
  });
}

watch(() => props.paymentModalHeight, (height) => {
  if (!isVisible.value) {
    currentPaymentModalHeight.value = height;
  }
});

onMounted(async () => {
  const needsModelHint = usesSharedCardForm.value
    && isAndroid
    && !isPixelFacebookDevice.value
    && IN_APP_BROWSER_UA.FACEBOOK.test(navigator.userAgent);
  if (!needsModelHint) return;

  isPixelFacebookDevice.value = isPixelFacebook(navigator.userAgent, await getDeviceModel(navigator));
});

onBeforeUnmount(() => {
  isVisible.value = false;
  document.body.classList.remove('overflow-hidden');
  document.documentElement.classList.remove('overflow-hidden');
  globalThis.removeEventListener('popstate', handlePopState);
});
</script>

<style lang="scss" scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 101;

  background: rgba(17, 17, 19, 0.75);

  width: 100%;
  max-width: none;
  height: 100dvh;
  max-height: none;
  margin: 0;
  border: 0;
  padding: 0;
  color: inherit;
  overflow: hidden;
  overscroll-behavior: none;
  touch-action: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.payment-modal {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: var(--payment-modal-height, auto);
  transition: height 0.25s ease;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;

  &__header {
    & h1 {
      will-change: contents;
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
    }

    @media (max-width: 400px) {
      & h1 {
        font-size: 18px;
      }
      & .en {
        font-size: 20px;
      }
    }
  }

  &__container {
    position: relative;
    max-width: 600px;
    margin: 0 auto;
    background: $white;
    border-radius: 16px 16px 0 0;
    padding: 24px 0 50px;
    height: var(--payment-modal-height, 95dvh);
    transition: height 0.25s ease;
    overflow-y: auto;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    will-change: contents;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    &__container {
      scroll-behavior: auto;
    }
  }

  &--managed-viewport {
    bottom: calc(100% - var(--checkout-visible-bottom, 100%));
    height: auto;
    transition: none;

    .payment-modal__container {
      height: min(var(--payment-modal-height, 95dvh), var(--checkout-visible-height, 100dvh));
      transition: none;
      scroll-behavior: auto;
    }
  }

  &--focus-space :deep(.card-form) {
    padding-bottom: var(--checkout-focus-space, 0px);
  }

  &__title {
    margin-bottom: 16px;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: transparent;
    border: none;
    will-change: transform;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    z-index: 100;

    & .nuxt-icon {
      color: $icon-gray;
      line-height: 0;
      font-size: 32px;
    }
  }
}

.blocker {
  min-height: 100vh;
  min-width: 100vw;
}

.slide-bottom-enter-active,
.slide-bottom-leave-active {
  transition: transform 0.5s ease;
}

.slide-bottom-enter-from,
.slide-bottom-leave-to {
  transform: translateY(100%);
}

.slide-bottom-enter-to,
.slide-bottom-leave-from {
  transform: translateY(0);
}
</style>
